import React, { ChangeEvent, useState } from 'react';
import { FormControlLabel, Grid, Typography } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import {
    CustomChipInputField,
    DragAndDrop,
    Radio,
    RadioGroup,
    TextField,
} from '@biq/apex-ui';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import InvalidPermissionsDialog from '../../../../components/invalid-permissions-dialog/InvalidPermissionsDialog';
import { DICTIONARY_PATHS } from '../../../../constants/dictionary-paths';
import { useAuth, usePermission } from '../../../../core';
import { usePredictDictionary } from '../../../../hooks';
import { Dictionary } from '../../../../lib';
import { JobFieldHeadingWithTooltip } from '../../../job-manager';
import {
    FileData,
    replaceModalConfig,
} from '../../../job-manager/components/create-job-dialog';
import { dictionaryConfig } from '../../dicitionary.helper';
import {
    predictDictionaryDefaults,
    PredictDictionaryFormInputs,
    predictDictionaryRadioButtons,
    PredictDictionarySchema,
    predictDictionaryStyles,
    PredictRadioButtons,
} from './import-dictionary-helper';
import { CreateFormFeatureDialog, MultilineTextField } from 'components';
import { useNavigate } from 'react-router-dom';

export const PredictDictionary = (): React.JSX.Element => {
    const { getToken } = useAuth();
    const { hasPermissions } = usePermission();
    const navigate = useNavigate();

    const [files, setFiles] = useState<FileData[]>([]);
    const [displayPermissionDialog, setDisplayPermissionDialog] =
        useState(false);
    const [selectedRadioButton, setSelectedRadioButton] =
        useState<PredictRadioButtons>('');
    const [fileError, setFileError] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [primaryIndexColumns, setPrimaryIndexColumns] = useState<string[]>(
        []
    );

    const { mutate: predictDictionary } = usePredictDictionary();

    const {
        watch,
        handleSubmit,
        control,
        formState: { errors },
    } = useForm<PredictDictionaryFormInputs>({
        mode: 'all',
        resolver: yupResolver(PredictDictionarySchema),
        defaultValues: predictDictionaryDefaults,
    });

    const handlePredictDictionary = (): void => {
        if (files.length === 0) {
            setFileError(true);
        } else {
            setFileError(false);
            setIsLoading(true);
            predictDictionary(
                {
                    file: files[0],
                    token: getToken(),
                    sheetName: watch('sheetName'),
                    sheetNum: watch('sheetNumber'),
                    description: watch('description'),
                    maxRowsToUse: watch('maxNumberOfRows'),
                    primaryIndexCols: primaryIndexColumns,
                },
                {
                    onSuccess: (response: any) => {
                        const predictedDictionary: Dictionary | undefined =
                            response?.data?.dictionary;

                        if (predictedDictionary) {
                            setTimeout(() => {
                                navigate(
                                    DICTIONARY_PATHS.CREATE_NEW_DICTIONARY,
                                    { state: predictedDictionary }
                                );
                            }, 500);
                        }
                        setIsLoading(false);
                    },
                    onError: () => {
                        setIsLoading(false);
                    },
                }
            );
        }
    };

    const onSubmit = (): void => {
        const permission = hasPermissions(['createData', 'createMetadata']);
        if (permission) {
            handleSubmit(handlePredictDictionary)();
        } else {
            setDisplayPermissionDialog(true);
        }
    };

    const handlePermissionDialogClose = (): void => {
        setDisplayPermissionDialog(false);
    };

    const handleCloseButton = (): void => {
        navigate(`${DICTIONARY_PATHS.LANDING_PAGE}?option=Predict Dictionary`);
    };

    const handleBackButton = (): void => {
        navigate(
            `${DICTIONARY_PATHS.CREATE_NEW_DICTIONARY_VIEW}?option=Predict Dictionary`
        );
    };

    return (
        <CreateFormFeatureDialog
            buttonText={'Start Upload'}
            handleSubmit={onSubmit}
            displaySubmitButton
            isLoading={isLoading}
            handleCloseButton={handleCloseButton}
            handleBackButton={handleBackButton}
            listConfig={dictionaryConfig}
        >
            <Grid sx={predictDictionaryStyles.mainContainer}>
                <Grid>
                    <Typography
                        sx={predictDictionaryStyles.subHeadingTextStyles}
                    >
                        Uses a sample data file (CSV, TAB, PIPE, XLS, XLSX,
                        JSON, PARQUET) to predict a dictionary definition. The
                        dictionary and optional mapping output will be exported.
                    </Typography>
                </Grid>
                <Grid sx={predictDictionaryStyles.descriptionFieldContainer}>
                    <MultilineTextField
                        control={control}
                        label='Description'
                        name='description'
                        error={!!errors.description}
                        errorText={
                            errors.description ? errors.description.message : ''
                        }
                    />
                </Grid>
                <Grid sx={predictDictionaryStyles.fieldContainer}>
                    <Grid>
                        <JobFieldHeadingWithTooltip
                            label='Primary Index Columns'
                            title={
                                'For primary index columns, enter your best guess. After the dictionary is returned, verify that the indexes attributes exist in the dataset and map to the predicted attributes defined in the dictionary.'
                            }
                            popperStyles={predictDictionaryStyles.popperStyles}
                            modifiers={[
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 4],
                                    },
                                },
                            ]}
                        />
                    </Grid>
                    <CustomChipInputField
                        customStyles={predictDictionaryStyles.customChipField}
                        required={false}
                        fieldValue={primaryIndexColumns}
                        setFieldValue={setPrimaryIndexColumns}
                        size='medium'
                        helperText='Separate multiple columns with commas'
                    />
                </Grid>
                <Grid sx={predictDictionaryStyles.maxNumberOfRowsField}>
                    <Controller
                        name='maxNumberOfRows'
                        control={control}
                        render={({ field }): React.JSX.Element => (
                            <TextField
                                {...field}
                                variant='outlined'
                                label='Max Rows to Use'
                                helperText='0-10000'
                                onChange={(value): void => {
                                    field.onChange(value);
                                }}
                                error={!!errors?.maxNumberOfRows}
                                errorText={
                                    errors.maxNumberOfRows
                                        ? errors?.maxNumberOfRows?.message
                                        : ''
                                }
                            />
                        )}
                    />
                </Grid>
                <Grid sx={predictDictionaryStyles.fieldContainer}>
                    <RadioGroup
                        label='Select Sheet By'
                        onChange={(
                            event: ChangeEvent<HTMLInputElement>
                        ): void => {
                            setSelectedRadioButton(event.target.value as any);
                        }}
                    >
                        <Grid
                            sx={
                                predictDictionaryStyles.radioButtonFormControlLabelContainer
                            }
                        >
                            <FormControlLabel
                                key={predictDictionaryRadioButtons[0].value}
                                value={predictDictionaryRadioButtons[0].value}
                                control={<Radio />}
                                label={predictDictionaryRadioButtons[0].label}
                                sx={predictDictionaryStyles.radioButton}
                            />
                            {selectedRadioButton === 'sheetName' && (
                                <Grid
                                    sx={
                                        predictDictionaryStyles.radioButtonFieldContainer
                                    }
                                >
                                    <Controller
                                        name='sheetName'
                                        control={control}
                                        render={({
                                            field,
                                        }): React.JSX.Element => (
                                            <TextField
                                                {...field}
                                                variant='outlined'
                                                onChange={(value): void => {
                                                    field.onChange(value);
                                                }}
                                                error={!!errors?.sheetName}
                                                errorText={
                                                    errors.sheetName
                                                        ? errors?.sheetName
                                                              ?.message
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                            <FormControlLabel
                                key={predictDictionaryRadioButtons[1].value}
                                value={predictDictionaryRadioButtons[1].value}
                                control={<Radio />}
                                label={predictDictionaryRadioButtons[1].label}
                                sx={predictDictionaryStyles.radioButton}
                            />
                            {selectedRadioButton === 'sheetNumber' && (
                                <Grid
                                    sx={
                                        predictDictionaryStyles.radioButtonFieldContainer
                                    }
                                >
                                    <Controller
                                        name='sheetNumber'
                                        control={control}
                                        render={({
                                            field,
                                        }): React.JSX.Element => (
                                            <TextField
                                                {...field}
                                                variant='outlined'
                                                onChange={(value): void => {
                                                    field.onChange(value);
                                                }}
                                                error={!!errors?.sheetNumber}
                                                errorText={
                                                    errors.sheetNumber
                                                        ? errors?.sheetNumber
                                                              ?.message
                                                        : ''
                                                }
                                            />
                                        )}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </RadioGroup>
                </Grid>
                <Grid sx={predictDictionaryStyles.containerStyles}>
                    <Grid>
                        <DragAndDrop
                            files={files}
                            setFiles={setFiles}
                            fileError={fileError}
                            setFileError={setFileError}
                            filePreview={true}
                            showReplaceFileModal={true}
                            replaceModalConfig={replaceModalConfig}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {displayPermissionDialog && (
                <InvalidPermissionsDialog
                    open={displayPermissionDialog}
                    onClose={handlePermissionDialogClose}
                />
            )}
        </CreateFormFeatureDialog>
    );
};
